import React, { useState, useRef, useEffect, Fragment } from 'react';
import { PencilIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import { getConfig } from '../../config/config-helper';
import dayjs from 'dayjs';
import MarkdownEditor from './MarkdownEditor';
import InputDatepicker from './InputDatepicker';
import { Input } from './Input';
import Button from './Button';
import { dateTimeFormat } from '../../utilities/constants';
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react';

interface EditableFieldProps {
  value: string | number;
  eventSlug: string;
  fieldName: string;
  token: string;
  isDate?: boolean; // Whether the field is a date field
  fieldType?: string; // Input type (text, datetime, etc.)
  isMarkdown?: boolean; // Whether the field is a markdown field
  markdownEventName?: string; // Event name for markdown variable replacement
  onUpdate?: () => void; // Optional callback after successful update
  validate?: (
    value: string | number,
    fieldValue: string | number,
  ) => {
    isValid: boolean;
    error: string | null;
  }; // Optional validation function
  confirmationMessage?: string; // Message for the confirmation dialog
  isAdmin?: boolean; // Whether the user is an admin and can edit fields
  parseValue?: (value: string | number) => any; // Optional function to parse value before sending to API
}

export default function EditableField({
  value,
  eventSlug,
  fieldName,
  token,
  isDate = false,
  fieldType = 'text',
  isMarkdown = false,
  markdownEventName = '',
  onUpdate,
  validate,
  confirmationMessage,
  isAdmin = false,
  parseValue,
}: EditableFieldProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [fieldValue, setFieldValue] = useState(value);
  const [isHovering, setIsHovering] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [saveSuccessfulMessage, setSaveSuccessfulMessage] = useState<string | null>(null);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const fieldRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const { dataopsAdminAppApiEndpoint } = getConfig();

  useEffect(() => {
    // Focus the input when entering edit mode
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  useEffect(() => {
    // Update local state when prop value changes
    setFieldValue(value);
  }, [value]);

  useEffect(() => {
    // Validate the field value when it changes
    if (validate && isEditing) {
      const { isValid, error } = validate(value, fieldValue);
      if (!isValid) {
        setError(error);
      } else {
        setError(null);
      }
    }
  }, [value, fieldValue]);

  // Format date for API submission
  const getSubmissionValue = () => {
    if (!isDate) return fieldValue;
    // If editing a date field, we need to convert to the ISO format for the API
    return dayjs(fieldValue).format();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFieldValue(e.target.value);
  };

  // MARK: handleSave
  const handleSave = async () => {
    setError(null);

    if (validate) {
      const { isValid, error } = validate(value, fieldValue);
      if (!isValid) {
        setError(error);
        return;
      }
    }

    // If confirmation is required, show the confirmation dialog
    if (confirmationMessage) {
      setIsConfirmationOpen(true);
      return;
    }

    // Otherwise proceed with saving
    await processSave(fieldValue);
  };

  // MARK: processSave
  const processSave = async (newValue: string | number) => {
    setIsLoading(true);
    try {
      const url = `${dataopsAdminAppApiEndpoint}/event_management/${eventSlug}`;

      // Determine the value to send based on field type and parseValue function
      let valueToSend;
      if (isDate) {
        valueToSend = getSubmissionValue();
      } else if (parseValue) {
        valueToSend = parseValue(newValue);
      } else {
        valueToSend = newValue;
      }

      const payload = {
        [fieldName]: valueToSend,
      };

      await axios.patch(url, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setIsEditing(false);
      if (onUpdate) {
        onUpdate();
      }
      // Set saveSuccessful state to true for 5 seconds then set back to false
      setSaveSuccessfulMessage('Save successful!');
      setTimeout(() => {
        setSaveSuccessfulMessage(null);
      }, 5000);
    } catch (err: any) {
      console.error('Error updating field:', err);

      // Extract error message from API response
      let errorMessage = 'Failed to update field';

      if (
        err.response?.data?.detail &&
        Array.isArray(err.response.data.detail) &&
        err.response.data.detail.length > 0
      ) {
        // API returns validation errors in a detail array with structured error info
        const firstError = err.response.data.detail[0];
        // Prefer ctx.error for cleaner message, fall back to msg
        errorMessage = firstError.ctx?.error || firstError.msg || errorMessage;
      } else if (err.response?.data?.message) {
        // Fall back to message field if present
        errorMessage = err.response.data.message;
      }

      setError(errorMessage);
    } finally {
      setIsLoading(false);
      setIsConfirmationOpen(false);
    }
  };

  // Handle clicking outside to cancel edit
  const handleClickOutside = (e: MouseEvent) => {
    if (
      isEditing &&
      fieldRef.current &&
      !fieldRef.current.contains(e.target as Node) &&
      dialogRef.current &&
      !dialogRef.current.contains(e.target as Node)
    ) {
      setIsEditing(false);
      setFieldValue(value); // Reset to original value
      setError(null); // Reset error state
    }
  };

  useEffect(() => {
    // Add click listener when in edit mode
    if (isEditing) {
      document.addEventListener('mouseup', handleClickOutside);
    } else {
      document.removeEventListener('mouseup', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mouseup', handleClickOutside);
    };
  }, [isEditing, value]);

  // Handle key presses
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      void handleSave();
    } else if (e.key === 'Escape') {
      setIsEditing(false);
      setFieldValue(value); // Reset to original value
    }
  };

  // MARK: Partials
  const datepickerPartial = () => {
    return (
      <InputDatepicker
        id={'dateInput_' + fieldName}
        dateValue={{
          startDate: dayjs(fieldValue).toDate(),
          endDate: dayjs(fieldValue).toDate(),
        }}
        setDateValue={(value) => {
          setFieldValue(dayjs(value?.startDate).format(dateTimeFormat));
        }}
        asSingle={true}
        useRange={false}
        enableTimePicker={fieldType === 'datetime'}
      />
    );
  };

  const markdownPartial = () => {
    return (
      <MarkdownEditor
        id={'markdownEditor'}
        value={fieldValue.toString()}
        onChange={(newValue) => {
          setFieldValue(newValue);
        }}
        eventName={markdownEventName}
        hideEditButton={false}
        hidePreviewButton={false}
        initialPreviewMode={false}
        handleSave={handleSave}
        handleCancel={() => {
          setIsEditing(false);
          setFieldValue(value); // Reset to original value
        }}
        isLoading={isLoading}
      />
    );
  };

  const inputPartial = () => {
    return (
      <Input
        customRef={inputRef}
        type={fieldType}
        value={fieldValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        disabled={isLoading}
      />
    );
  };

  const editingPartial = () => {
    return (
      <div ref={fieldRef} className={`flex ${isMarkdown ? 'flex-col' : 'items-center'} justify-between gap-2`}>
        <div className="w-full">{isDate ? datepickerPartial() : isMarkdown ? markdownPartial() : inputPartial()}</div>
        {!isMarkdown && (
          <div className="flex gap-2">
            <Button
              size="small"
              intent="primary"
              onClick={() => {
                void handleSave();
              }}
              disabled={isLoading}
            >
              Save
            </Button>
            <Button
              size="small"
              intent="light"
              onClick={() => {
                setIsEditing(false);
                setFieldValue(value); // Reset to original value
              }}
              disabled={isLoading}
            >
              Cancel
            </Button>
          </div>
        )}
      </div>
    );
  };

  const notEditingPartial = () => {
    return (
      <>
        {isMarkdown ? (
          <div className={'flex flex-col gap-2'}>
            {saveSuccessfulMessage && <p className="text-green-600 text-xs">{saveSuccessfulMessage}</p>}
            <div
              className={`w-full relative ${isAdmin ? 'cursor-pointer' : ''}`}
              onClick={() => {
                if (isAdmin) {
                  setIsEditing(true);
                }
              }}
            >
              <MarkdownEditor
                id={'markdownEditorReadOnly'}
                value={fieldValue.toString()}
                onChange={() => {}}
                eventName={markdownEventName}
                hideEditButton={!isAdmin}
                hidePreviewButton={true}
                initialPreviewMode={true}
              />
            </div>
          </div>
        ) : (
          <div className={'flex items-center'}>
            <span>{fieldValue}</span>
            {isHovering && isAdmin && (
              <button
                onClick={() => {
                  setIsEditing(true);
                }}
                className="ml-2 text-gray-400 hover:text-dataops-primary-dark-blue"
              >
                <PencilIcon className="h-4 w-4" />
              </button>
            )}
            {saveSuccessfulMessage && <p className="text-green-600 text-xs ml-4">{saveSuccessfulMessage}</p>}
          </div>
        )}
      </>
    );
  };

  // MARK: Confirmation Modal
  const confirmationModal = () => {
    return (
      <Transition show={isConfirmationOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-40"
          ref={dialogRef}
          onClose={() => {
            setIsConfirmationOpen(false);
          }}
        >
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </TransitionChild>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <TransitionChild
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <DialogPanel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-md">
                  <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                    <div className="sm:flex sm:items-start">
                      <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                        <DialogTitle as="h3" className="text-base font-semibold leading-6 text-gray-900">
                          Confirm Update
                        </DialogTitle>
                        <div className="mt-2">
                          <p className="text-sm text-gray-500">{confirmationMessage}</p>
                          <p className="text-sm text-gray-800">{value.toString() + ' -> ' + fieldValue.toString()} </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                    <Button
                      onClick={() => {
                        void processSave(fieldValue);
                      }}
                      intent="primary"
                      size="small"
                      className="ml-3"
                    >
                      Confirm
                    </Button>
                    <Button
                      onClick={() => {
                        setIsConfirmationOpen(false);
                      }}
                      intent="light"
                      size="small"
                    >
                      Cancel
                    </Button>
                  </div>
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </Dialog>
      </Transition>
    );
  };

  // MARK: Return
  return (
    <>
      <div
        className=""
        onMouseEnter={() => {
          setIsHovering(true);
        }}
        onMouseLeave={() => {
          setIsHovering(false);
        }}
      >
        {isEditing ? editingPartial() : notEditingPartial()}
        {error && <p className="text-red-600 text-xs mt-1">{error}</p>}
      </div>
      {confirmationModal()}
    </>
  );
}
